<!-- Base.Select.vue (client-lms) -->
<template>
  <div class="lms_select_wrap" :class="[{ open: isOpen, disabled: disabled, error: hasError }, size === 'sm' ? 'lms_select_sm' : '']">
    <button
      ref="selectBtn"
      type="button"
      class="lms_select_trigger"
      :style="size === 'sm' ? { height: '36px', padding: '0 8px 0 14px', fontSize: '14px', gap: '4px', width: '80px', minWidth: '80px' } : {}"
      :disabled="disabled"
      @click="toggleDropdown"
      @blur="onBlur($event)"
    >
      <span class="lms_select_value" :class="{ placeholder: !selectedLabel }">
        {{ selectedLabel || placeholder }}
      </span>
      <i
        class="mdi mdi-chevron-down lms_select_arrow"
        :style="{ fontSize: size === 'sm' ? '16px' : '20px' }"
      ></i>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="dropdownRef"
        class="lms_select_dropdown"
        :style="dropdownStyle"
      >
        <div v-if="searchable" class="lms_select_search" @mousedown.stop>
          <i class="mdi mdi-magnify"></i>
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="lms_select_search_input"
            :placeholder="searchPlaceholder"
            @mousedown.stop
            @blur="onSearchBlur($event)"
          />
        </div>
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="lms_select_option"
          :class="{ selected: selectedValue === option.value }"
          @mousedown.prevent="selectOption(option)"
        >
          {{ option.label }}
        </div>
        <div v-if="searchable && filteredOptions.length === 0" class="lms_select_empty">
          검색 결과가 없습니다
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, type PropType, nextTick } from "vue";

const selectedValue = defineModel<string | number | null>();

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
}>();

const props = defineProps({
  placeholder: { type: String, default: "선택" },
  options: {
    type: Array as PropType<{ label: string; value: string | number }[]>,
    default: () => [],
  },
  disabled: { type: Boolean, default: false },
  errorMessages: { type: Array as PropType<string[]>, default: () => [] },
  size: { type: String, default: "" }, // 'sm' for small
  searchable: { type: Boolean, default: false },
  searchPlaceholder: { type: String, default: "검색어를 입력해 주세요" },
});

const isOpen = ref(false);
const selectBtn = ref<HTMLButtonElement | null>(null);
const dropdownRef = ref<HTMLDivElement | null>(null);
const dropdownStyle = ref<Record<string, string>>({});
const searchQuery = ref("");
const searchInputRef = ref<HTMLInputElement | null>(null);

const filteredOptions = computed(() => {
  if (!props.searchable || !searchQuery.value.trim()) return props.options;
  const q = searchQuery.value.trim().toLowerCase();
  return props.options.filter((o) => o.label.toLowerCase().includes(q));
});

const hasError = computed(() => props.errorMessages.length > 0);

const selectedLabel = computed(() => {
  const found = props.options.find((o) => o.value === selectedValue.value);
  return found?.label || "";
});

const toggleDropdown = async () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    await nextTick();
    positionDropdown();
    if (props.searchable) searchInputRef.value?.focus();
  } else {
    searchQuery.value = "";
  }
};

const positionDropdown = async () => {
  if (!selectBtn.value) return;
  const rect = selectBtn.value.getBoundingClientRect();

  // 기본: 아래로 열기
  dropdownStyle.value = {
    position: "fixed",
    top: `${rect.bottom + 4}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    zIndex: "10100",
  };

  // 다음 틱에서 실제 드롭다운 높이 확인 후 위로 전환
  await nextTick();
  if (dropdownRef.value) {
    const ddRect = dropdownRef.value.getBoundingClientRect();
    if (ddRect.bottom > window.innerHeight) {
      dropdownStyle.value = {
        position: "fixed",
        bottom: `${window.innerHeight - rect.top + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: "10100",
      };
    }
  }
};

const onBlur = (e: FocusEvent) => {
  const next = e.relatedTarget as HTMLElement | null;
  // 드롭다운 내부(검색 input 포함)로 포커스 이동이면 닫지 않음
  if (next && dropdownRef.value?.contains(next)) return;
  setTimeout(() => {
    isOpen.value = false;
  }, 150);
};

const onSearchBlur = (e: FocusEvent) => {
  const next = e.relatedTarget as HTMLElement | null;
  // 옵션 클릭 중이면 닫지 않음 (selectOption이 처리)
  if (next && dropdownRef.value?.contains(next)) return;
  setTimeout(() => {
    isOpen.value = false;
    searchQuery.value = "";
  }, 150);
};

const selectOption = (option: { label: string; value: string | number }) => {
  selectedValue.value = option.value;
  emit("update:modelValue", option.value);
  isOpen.value = false;
  searchQuery.value = "";
};
</script>

<style scoped>
.lms_select_wrap {
  display: flex;
  position: relative;
  width: 100%;
}

.lms_select_trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 48px;
  padding: 0 8px 0 12px;
  border: 1px solid var(--lms-outline-gray-main, #E0E0E0);
  border-radius: 4px;
  background-color: var(--lms-surface, #FFFFFF);
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: var(--lms-on-surface-high, rgba(0, 0, 0, 0.8));
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.lms_select_trigger:hover {
  border-color: var(--lms-outline-black, #AAABAB);
}

.lms_select_wrap.open .lms_select_trigger {
  border-color: var(--lms-primary-400, #5C74F6);
  border-width: 2px;
  padding: 0 7px 0 11px;
}

.lms_select_wrap.disabled .lms_select_trigger {
  background-color: var(--lms-grey-100, #F6F7F7);
  color: var(--lms-on-surface-low-var, rgba(0, 0, 0, 0.18));
  cursor: not-allowed;
}

.lms_select_wrap.error .lms_select_trigger {
  border-color: var(--lms-outline-error, #FF1A2D);
}

.lms_select_value.placeholder {
  color: var(--lms-on-surface-low, rgba(0, 0, 0, 0.35));
}

.lms_select_arrow {
  flex-shrink: 0;
  color: var(--lms-on-surface-low, rgba(0, 0, 0, 0.35));
  transition: transform 0.2s;
}

.lms_select_wrap.open .lms_select_arrow {
  transform: rotate(180deg);
}

/* Small size */
.lms_select_wrap.lms_select_sm {
  width: auto;
  display: inline-flex;
}

.lms_select_wrap.small .lms_select_trigger,
.lms_select_wrap.lms_select_sm .lms_select_trigger {
  height: 32px;
  padding: 0 4px 0 10px;
  gap: 4px;
  font-size: 13px;
  border-radius: 4px;
}

.lms_select_wrap.small .lms_select_arrow {
  font-size: 16px;
}
</style>

<style>
.lms_select_dropdown {
  background-color: #FFFFFF;
  border-radius: 4px;
  box-shadow: 0px 1px 3px 0px rgba(145, 158, 171, 0.12),
              0px 1px 1px 0px rgba(145, 157, 171, 0.14),
              0px 2px 1px -1px rgba(145, 158, 171, 0.2);
  padding: 8px 0;
  max-height: 240px;
  overflow-y: auto;
}

.lms_select_option {
  padding: 10px 16px;
  font-family: "Pretendard", sans-serif;
  font-size: 16px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.5);
  cursor: pointer;
  transition: background-color 0.15s;
}

.lms_select_option:hover {
  background-color: #F6F7F7;
}

.lms_select_option.selected {
  color: #5C74F6;
  font-weight: 600;
}

.lms_select_search {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-bottom: 1px solid #EAEBEB;
  margin: -8px 0 4px;
  background: #FFFFFF;
  position: sticky;
  top: 0;
  z-index: 1;
}
.lms_select_search i {
  font-size: 18px;
  color: rgba(0,0,0,0.35);
  flex-shrink: 0;
}
.lms_select_search_input {
  flex: 1;
  border: none;
  outline: none;
  font-family: "Pretendard", sans-serif;
  font-size: 14px;
  color: rgba(0,0,0,0.8);
  background: transparent;
  width: 0;
  min-width: 0;
}
.lms_select_search_input::placeholder {
  color: rgba(0,0,0,0.35);
}
.lms_select_empty {
  padding: 16px;
  text-align: center;
  font-family: "Pretendard", sans-serif;
  font-size: 13px;
  color: rgba(0,0,0,0.4);
}
</style>
